/**
 * @fileoverview AddRestaurantModal Component
 * Modal for searching and adding restaurants to a list using Google Places
 */

// External imports
import React, { useState, useRef, useEffect } from 'react';
import { MapPin, Star, DollarSign } from 'lucide-react';

// UI Components
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

// Internal imports
import PlacesAutocomplete from '../../map/components/places-autocomplete';
import { useSaveRestaurant } from '../../restaurants/hooks/use-save-restaurant';
import { useAddToList } from '../hooks/use-lists';
import { GooglePlacesRestaurant } from '../../restaurants/types/restaurants.types';

interface AddRestaurantModalProps {
  listId?: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  isGoogleApiLoaded: boolean;
}

export const AddRestaurantModal: React.FC<AddRestaurantModalProps> = ({
  listId,
  isOpen,
  onOpenChange,
  isGoogleApiLoaded,
}) => {
  const [selectedRestaurant, setSelectedRestaurant] = useState<GooglePlacesRestaurant | null>(null);
  const placesAutocompleteRef = useRef<{ initializeServices: () => void }>(null);

  const saveRestaurant = useSaveRestaurant();
  const addToList = useAddToList();

  // Initialize Places Autocomplete when Google API is loaded
  useEffect(() => {
    if (isGoogleApiLoaded && isOpen) {
      placesAutocompleteRef.current?.initializeServices();
    }
  }, [isGoogleApiLoaded, isOpen]);

  // Handle place selection from autocomplete
  const handlePlaceSelect = (place: GooglePlacesRestaurant) => {
    setSelectedRestaurant(place);
  };

  // Handle saving/adding restaurant
  const handleSaveRestaurant = async () => {
    if (!selectedRestaurant) return;

    try {
      // Step 1: Save restaurant to database (if not already saved)
      const saveResponse = await saveRestaurant.mutateAsync(selectedRestaurant);

      // Step 2: If listId provided, add to the list
      if (listId) {
        const restaurantId = saveResponse.data.restaurantId;
        await addToList.mutateAsync({
          listId,
          restaurantId,
        });
      }

      // Step 3: Close modal and reset state
      setSelectedRestaurant(null);
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to save restaurant:', error);
      // Error toasts are handled by the hooks
    }
  };

  // Reset state when modal closes
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setSelectedRestaurant(null);
    }
    onOpenChange(open);
  };

  const getPriceDisplay = (priceLevel?: number) => {
    if (priceLevel === undefined) return null;
    return '$'.repeat(priceLevel + 1);
  };

  const isLoading = saveRestaurant.isPending || (listId ? addToList.isPending : false);

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>{listId ? 'Add Restaurant to List' : 'Save Restaurant'}</DialogTitle>
          <DialogDescription>
            {listId
              ? 'Search for a restaurant using Google Places and add it to your list.'
              : 'Search for a restaurant using Google Places and save it to your favorites.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Loading state */}
          {!isGoogleApiLoaded ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <span className="ml-3 text-muted-foreground">Loading Google Maps...</span>
            </div>
          ) : (
            <>
              {/* Search Input */}
              <PlacesAutocomplete
                ref={placesAutocompleteRef}
                onPlaceSelect={handlePlaceSelect}
                placeholder="Search for restaurants..."
              />

              {/* Restaurant Preview */}
              {selectedRestaurant ? (
                <Card className="p-4">
                  <div className="space-y-3">
                    <div>
                      <h3 className="text-lg font-bold">{selectedRestaurant.name}</h3>

                      {/* Rating and Price */}
                      <div className="flex items-center gap-4 mt-2">
                        {selectedRestaurant.rating && (
                          <div className="flex items-center gap-1">
                            <Star className="h-4 w-4 fill-star text-star-stroke" />
                            <span className="text-sm font-medium">
                              {selectedRestaurant.rating.toFixed(1)}
                            </span>
                          </div>
                        )}
                        {selectedRestaurant.price_level !== undefined && (
                          <div className="flex items-center gap-1">
                            <DollarSign className="h-4 w-4 text-foreground" />
                            <span className="text-sm font-medium">
                              {getPriceDisplay(selectedRestaurant.price_level)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Address */}
                    <div className="flex items-start gap-2">
                      <MapPin className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-muted-foreground">
                        {selectedRestaurant.formatted_address}
                      </p>
                    </div>

                    {/* Restaurant Types */}
                    {selectedRestaurant.types && selectedRestaurant.types.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {selectedRestaurant.types.slice(0, 3).map((type, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 bg-muted text-muted-foreground rounded-md text-xs capitalize"
                          >
                            {type.replace(/_/g, ' ')}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </Card>
              ) : (
                <Card className="p-8 text-center">
                  <MapPin className="mx-auto mb-2 h-12 w-12 text-muted-foreground opacity-50" />
                  <p className="text-sm text-muted-foreground">
                    Search for a restaurant to see preview
                  </p>
                </Card>
              )}
            </>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveRestaurant}
            disabled={!selectedRestaurant || isLoading}
          >
            {isLoading ? (
              <>
                <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                {listId ? 'Adding...' : 'Saving...'}
              </>
            ) : (
              listId ? 'Add to List' : 'Save Restaurant'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
